import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useMemo, useState } from "react";
import { ChevronRight, Grid3x3, Loader2, Lock, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** Every app against every audience. Ticking a box saves that app straight
 *  away, so a pass through 29 apps is one sitting, not 29 dialogs. */
export default function AppAudienceGrid() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [q, setQ] = usePersistedState("app-audiences.q", "");
  const [walk, setWalk] = useState(false);
  const [step, setStep] = useState(0);
  const [presets, setPresets] = useState([]);
  const [preset, setPreset] = useState("");
  const [sel, setSel] = useState({});          // app_id -> picked for bulk apply
  const [newName, setNewName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/app-audiences/grid");
      setData(d);
    } catch {
      toast.error("Couldn't load the app audience grid");
    } finally { setLoading(false); }
  };
  const loadPresets = () => api.get("/app-audience-presets")
    .then((r) => setPresets(r.data.presets || [])).catch(() => setPresets([]));
  useEffect(() => { load(); loadPresets(); }, []);

  const chosenApps = Object.entries(sel).filter(([, v]) => v).map(([k]) => k);

  const applyPreset = async () => {
    if (!preset || !chosenApps.length) return;
    try {
      const { data: r } = await api.post(`/app-audience-presets/${preset}/apply`,
        { app_ids: chosenApps });
      toast.success(`Applied to ${r.applied} app${r.applied === 1 ? "" : "s"}`);
      setSel({});
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't apply that preset");
    }
  };

  // A preset is made from one app you've already got right.
  const savePreset = async () => {
    const app = apps.find((a) => a.app_id === chosenApps[0]);
    if (!app || !newName.trim()) return;
    try {
      await api.post("/app-audience-presets",
        { label: newName.trim(), audiences: app.audiences || [] });
      toast.success(`Saved "${newName.trim()}" — ${(app.audiences || []).length} audience(s)`);
      setNewName("");
      await loadPresets();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save that preset");
    }
  };

  const apps = data?.apps || [];
  const auds = data?.audiences || [];

  // "Nobody outside the office can see these yet" — the pass the user wants.
  const needsAttention = useMemo(
    () => apps.filter((a) => !a.audiences?.length
      || a.audiences.every((x) => x === "super" || x === "staff")),
    [apps]);

  const shown = useMemo(() => {
    const base = walk ? needsAttention : apps;
    const needle = q.trim().toLowerCase();
    return needle ? base.filter((a) => (a.name || "").toLowerCase().includes(needle)) : base;
  }, [apps, needsAttention, walk, q]);

  const current = walk ? shown[Math.min(step, Math.max(0, shown.length - 1))] : null;

  const toggle = async (app, audienceId) => {
    const next = app.audiences?.includes(audienceId)
      ? app.audiences.filter((x) => x !== audienceId)
      : [...(app.audiences || []), audienceId];
    setSaving(app.app_id);
    // Optimistic — the row you clicked shouldn't flicker back and forth.
    setData((d) => ({ ...d, apps: d.apps.map((a) => a.app_id === app.app_id
      ? { ...a, audiences: next } : a) }));
    try {
      await api.put(`/apps/${app.app_id}/audiences`, { audiences: next });
      const { data: d } = await api.get("/app-audiences/grid");
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save that");
      await load();
    } finally { setSaving(""); }
  };

  const cell = (app, a) => {
    const on = app.audiences?.includes(a.audience_id);
    const partner = (a.base_roles || []).includes("partner");
    // Super User is the floor on every app (iter 264) — shown, never toggled.
    const floor = a.audience_id === "super";
    return (
      <td key={a.audience_id} className="border-b border-slate-50 px-1 py-1 text-center dark:border-slate-800/60">
        <button type="button" data-testid={`grid-cell-${app.app_id}-${a.audience_id}`}
          onClick={() => !floor && toggle(app, a.audience_id)}
          disabled={floor || saving === app.app_id}
          title={floor ? "Super admins always have access" : `${on ? "Remove" : "Add"} ${a.label}`}
          className={`flex h-6 w-6 items-center justify-center rounded border text-xs transition-colors ${on || floor
            ? "border-primary bg-primary text-white"
            : "border-slate-200 bg-white hover:border-primary/50 dark:border-slate-700 dark:bg-slate-900"} ${
            partner && on ? "bg-emerald-600 border-emerald-600" : ""} ${floor ? "cursor-default opacity-80" : ""}`}>
          {floor ? <Lock className="h-3 w-3" /> : on ? "✓" : ""}
        </button>
      </td>
    );
  };

  return (
    <Card className="px-5 py-4" data-testid="app-audience-grid-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-slate-800 dark:text-slate-100">
            <Grid3x3 className="h-4 w-4 text-slate-400" /> Who sees each app
          </h3>
          <p className="mt-1 max-w-2xl text-xs text-slate-500 dark:text-slate-400">
            Tick an audience to give it the app — each tick saves on its own. The people
            column is the real head count, counting someone once even if they land in two
            audiences.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find an app"
            data-testid="app-grid-search" className="h-8 w-40 text-xs" />
          <Button size="sm" variant={walk ? "default" : "outline"} data-testid="app-grid-walk"
            onClick={() => { setWalk(!walk); setStep(0); }}>
            {walk ? "Show all apps" : `Walk the ${needsAttention.length} with no partners`}
          </Button>
        </div>
      </div>

      {walk && current && (
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2"
          data-testid="app-grid-walk-bar">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            {Math.min(step + 1, shown.length)} of {shown.length}: {current.name}
          </span>
          <span className="text-[11px] text-slate-500 dark:text-slate-400">
            Tick the partner types that should see it, then move on.
          </span>
          <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs"
            data-testid="app-grid-walk-next"
            onClick={() => setStep((s) => Math.min(s + 1, shown.length - 1))}>
            Next app <ChevronRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/40"
        data-testid="app-grid-presets">
        <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
          {chosenApps.length ? `${chosenApps.length} app${chosenApps.length === 1 ? "" : "s"} picked` : "Pick apps to use a preset"}
        </span>
        <select value={preset} onChange={(e) => setPreset(e.target.value)}
          data-testid="app-grid-preset-select"
          className="h-7 rounded-md border border-slate-200 bg-white px-2 text-xs dark:border-slate-700 dark:bg-slate-900">
          <option value="">Choose a preset…</option>
          {presets.map((p) => (
            <option key={p.preset_id} value={p.preset_id}>{p.label} ({p.audiences.length})</option>
          ))}
        </select>
        <Button size="sm" className="h-7 text-xs" onClick={applyPreset}
          disabled={!preset || !chosenApps.length} data-testid="app-grid-preset-apply">
          Apply to {chosenApps.length || 0}
        </Button>
        <span className="mx-1 h-4 w-px bg-slate-200 dark:bg-slate-700" />
        <Input value={newName} onChange={(e) => setNewName(e.target.value)}
          placeholder="Save 1 app's ticks as…" data-testid="app-grid-preset-name"
          className="h-7 w-44 text-xs" />
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={savePreset}
          disabled={chosenApps.length !== 1 || newName.trim().length < 2}
          data-testid="app-grid-preset-save">
          Save preset
        </Button>
        {preset && (
          <button type="button" data-testid="app-grid-preset-delete"
            onClick={async () => {
              await api.delete(`/app-audience-presets/${preset}`);
              setPreset("");
              await loadPresets();
              toast.success("Preset deleted");
            }}
            className="text-[11px] text-slate-400 hover:text-rose-600">Delete preset</button>
        )}
      </div>

      <div className="mt-3 max-h-[70vh] overflow-auto rounded-lg border border-slate-100 dark:border-slate-800" data-testid="app-grid-scroll">
        {loading ? (
          <div className="flex h-24 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" />
          </div>
        ) : (
          <table className="w-full border-separate border-spacing-0 text-xs">
            <thead className="sticky top-0 z-20">
              <tr>
                <th className="sticky left-0 z-30 w-6 border-b border-slate-100 bg-card py-1.5 dark:border-slate-800" />
                <th className="sticky left-6 z-30 border-b border-slate-100 bg-card py-1.5 pr-2 text-left font-semibold text-slate-500 dark:border-slate-800 dark:text-slate-400">App</th>
                {auds.map((a) => (
                  <th key={a.audience_id} className="h-24 border-b border-slate-100 bg-card px-1 pb-1.5 align-bottom dark:border-slate-800">
                    <div className="mx-auto w-6 whitespace-nowrap text-left text-[11px] font-semibold text-slate-500 dark:text-slate-400"
                      style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
                      {a.label} ({data.counts?.[a.audience_id] ?? 0})
                    </div>
                  </th>
                ))}
                <th className="border-b border-slate-100 bg-card py-1.5 pl-2 pr-2 text-right font-semibold text-slate-500 dark:border-slate-800 dark:text-slate-400">People</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((app, i) => (
                <tr key={app.app_id} data-testid={`app-grid-row-${app.app_id}`}
                  className={`${
                    walk && current?.app_id === app.app_id ? "bg-primary/5" : ""} ${
                    i % 2 ? "bg-slate-50/40 dark:bg-slate-800/20" : "bg-card"}`}>
                  <td className="sticky left-0 z-10 border-b border-slate-50 bg-inherit py-1 pl-1 dark:border-slate-800/60">
                    <input type="checkbox" checked={!!sel[app.app_id]}
                      data-testid={`app-grid-pick-${app.app_id}`}
                      onChange={(e) => setSel((s2) => ({ ...s2, [app.app_id]: e.target.checked }))}
                      className="h-3.5 w-3.5 accent-primary" />
                  </td>
                  <td className="sticky left-6 z-10 border-b border-slate-50 bg-inherit py-1 pr-2 dark:border-slate-800/60">
                    <span className="font-medium text-slate-700 dark:text-slate-200">{app.name}</span>
                    {app.status !== "active" && (
                      <span className="ml-1.5 text-[10px] uppercase text-slate-400">{app.status}</span>
                    )}
                  </td>
                  {auds.map((a) => cell(app, a))}
                  <td className="border-b border-slate-50 py-1 pl-2 pr-2 text-right tabular-nums text-slate-500 dark:border-slate-800/60 dark:text-slate-400"
                    data-testid={`app-grid-reach-${app.app_id}`}>
                    {saving === app.app_id
                      ? <Loader2 className="ml-auto h-3 w-3 animate-spin" />
                      : <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{app.reach}</span>}
                  </td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr><td colSpan={auds.length + 3} className="py-4 text-center text-slate-400" data-testid="app-grid-empty">
                  {walk ? "Every app has an audience beyond staff — nothing left to walk." : "No apps match that."}
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}
